import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { IconFilter, IconShield } from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReactMultiSelect from '~/components/react_multi_select/react_multi_select'
import ReactSingleSelect from '~/components/react_single_select/react_single_select'
import ReactController from '~/controllers/react_controller'

// configuration mapping each tab to the set of filter fields it should display
const TAB_FILTER_FIELDS = {
  default: ['period', 'account_ids'],
  'service-desk': [
    'period',
    'account_ids',
    'issue_priorities',
    'issue_types',
    'request_types',
    'reporter_types',
    'issue_reporters',
    'issue_assignees',
  ],
  'patch-management': ['period', 'environments', 'account_ids'],
}

function filtersForTab(tab) {
  return TAB_FILTER_FIELDS[tab] || TAB_FILTER_FIELDS.default
}

function GuardianFilters({ currentTab, initialOptions = {}, initialValues = {}, onChange }) {
  const getDefaultFilters = useCallback(
    () => ({
      period: initialValues.period || initialOptions.period[0]?.value || '',
      account_ids: initialValues.account_ids || [],
      environments: initialValues.environments || [],
      reporter_types: initialValues.reporter_types || [],
      issue_priorities: initialValues.issue_priorities || [],
      issue_types: initialValues.issue_types || [],
      request_types: initialValues.request_types || [],
      issue_reporters: initialValues.issue_reporters || [],
      issue_assignees: initialValues.issue_assignees || [],
      guardian_tiers: initialValues.guardian_tiers || [],
    }),
    [initialValues, initialOptions],
  )

  const getDefaultOptions = useCallback(
    () => ({
      periodOptions: initialOptions.period || [],
      awsAccountOptions: initialOptions.aws_accounts || [],
      environmentOptions: initialOptions.environments || [],
      reporterTypesOptions: initialOptions.reporter_types || [],
      issuePrioritiesOptions: initialOptions.issue_priorities || [],
      issueTypesOptions: initialOptions.issue_types || [],
      requestTypesOptions: initialOptions.request_types || [],
      issueReportersOptions: initialOptions.issue_reporters || [],
      issueAssigneesOptions: initialOptions.issue_assignees || [],
      guardianTiersOptions: initialOptions.guardian_tiers || [],
    }),
    [initialOptions],
  )

  const { periodOptions, awsAccountOptions, environmentOptions } = getDefaultOptions()
  const {
    reporterTypesOptions,
    issuePrioritiesOptions,
    issueTypesOptions,
    requestTypesOptions,
    issueReportersOptions,
    issueAssigneesOptions,
    guardianTiersOptions,
  } = getDefaultOptions()

  const guardianDefaultTier = initialOptions.guardian_default_tier

  const visibleFilters = filtersForTab(currentTab)

  const [filters, setFilters] = useState(() => getDefaultFilters())
  const [isMounted, setIsMounted] = useState(false)
  // A submit fires only when this flag is set right before a state change. Period and tier
  // switches submit immediately; advanced (collapse panel) filters stage until "Apply".
  const submitOnNextRender = useRef(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: submit only when flagged
  useEffect(() => {
    if (!isMounted || !submitOnNextRender.current) {
      return
    }
    submitOnNextRender.current = false
    onChange(filters)
  }, [filters, isMounted])

  // Advanced (panel) filters: stage the change only — no submit until Apply.
  const handleFilterChange = useCallback((fieldName, value) => {
    setFilters((prev) => ({ ...prev, [fieldName]: value }))
  }, [])

  // Period: submit immediately on change.
  const handlePeriodChange = useCallback((fieldName, value) => {
    submitOnNextRender.current = true
    setFilters((prev) => ({ ...prev, [fieldName]: value }))
  }, [])

  // Apply the currently staged panel filters.
  const handleApply = useCallback(() => {
    submitOnNextRender.current = true
    setFilters((prev) => ({ ...prev }))
  }, [])

  // Reset only the panel filters — Period and tier selection are preserved.
  const handleReset = useCallback((e) => {
    e?.preventDefault()
    submitOnNextRender.current = true
    setFilters((prev) => ({
      ...prev,
      account_ids: [],
      environments: [],
      reporter_types: [],
      issue_priorities: [],
      issue_types: [],
      request_types: [],
      issue_reporters: [],
      issue_assignees: [],
    }))
  }, [])

  const activeTier = filters.guardian_tiers?.[0] || guardianDefaultTier
  const activeTierOption = guardianTiersOptions.find((o) => o.value === activeTier)

  // Tier switch: submit immediately (and clear account scope).
  const handleTierSelect = useCallback(
    (tier) => {
      if (activeTier === tier) return
      submitOnNextRender.current = true
      setFilters((prev) => ({ ...prev, guardian_tiers: [tier], account_ids: [] }))
    },
    [activeTier],
  )

  const FIELD_CONFIG = {
    period: {
      label: t`Period`,
      render: () => (
        <ReactSingleSelect
          id='period-filter'
          name='period'
          options={periodOptions}
          selected={filters.period}
          onChange={handlePeriodChange}
          classNamePrefix='period-select'
          size='sm'
        />
      ),
    },
    account_ids: {
      label: t`AWS Accounts`,
      render: () => (
        <ReactMultiSelect
          id='aws-accounts-filter'
          name='account_ids'
          options={awsAccountOptions}
          selected={filters.account_ids}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='aws-accounts-select'
        />
      ),
    },
    environments: {
      label: t`Environments`,
      render: () => (
        <ReactMultiSelect
          id='environments-filter'
          name='environments'
          options={environmentOptions}
          selected={filters.environments}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='environments-select'
        />
      ),
    },
    reporter_types: {
      label: t`Reporter Type`,
      render: () => (
        <ReactMultiSelect
          id='reporter-types-filter'
          name='reporter_types'
          options={reporterTypesOptions}
          selected={filters.reporter_types}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='reporter-types-select'
        />
      ),
    },
    issue_priorities: {
      label: t`Issue Priorities`,
      render: () => (
        <ReactMultiSelect
          id='issue-priorities-filter'
          name='issue_priorities'
          options={issuePrioritiesOptions}
          selected={filters.issue_priorities}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='issue-priorities-select'
        />
      ),
    },
    issue_types: {
      label: t`Issue Types`,
      render: () => (
        <ReactMultiSelect
          id='issue-types-filter'
          name='issue_types'
          options={issueTypesOptions}
          selected={filters.issue_types}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='issue-types-select'
        />
      ),
    },
    request_types: {
      label: t`Request Type`,
      render: () => (
        <ReactMultiSelect
          id='request-types-filter'
          name='request_types'
          options={requestTypesOptions}
          selected={filters.request_types}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='request-types-select'
        />
      ),
    },
    issue_reporters: {
      label: t`Issue Reporters`,
      render: () => (
        <ReactMultiSelect
          id='issue-reporters-filter'
          name='issue_reporters'
          options={issueReportersOptions}
          selected={filters.issue_reporters}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='issue-reporters-select'
        />
      ),
    },
    issue_assignees: {
      label: t`Issue Assignees`,
      render: () => (
        <ReactMultiSelect
          id='issue-assignees-filter'
          name='issue_assignees'
          options={issueAssigneesOptions}
          selected={filters.issue_assignees}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='issue-assignees-select'
        />
      ),
    },
    guardian_tiers: {
      label: t`Guardian Plan`,
      render: () => (
        <ReactMultiSelect
          id='guardian-tier-filter'
          name='guardian_tiers'
          options={guardianTiersOptions}
          selected={filters.guardian_tiers}
          onChange={handleFilterChange}
          size='sm'
          hideSelectAll
          classNamePrefix='guardian-tier-select'
        />
      ),
    },
  }

  // Period stays inline in the toolbar; every other filter lives in the collapsible panel.
  const advancedFields = visibleFilters.filter((f) => f !== 'period')

  const activeAdvancedCount = advancedFields.filter((f) => {
    const v = filters[f]
    return Array.isArray(v) ? v.length > 0 : Boolean(v)
  }).length

  return (
    <>
      {/* `.guardian-filters` uses display:contents (see SCSS) so all inline items
          join the toolbar flex row in the wrapper, while the panel spans the full width below. */}
      <div className='guardian-filters'>
        {/* Tier name + icon — start of the row */}
        {activeTierOption && (
          <div className='guardian-filters__tier-label d-flex align-items-center gap-1 text-muted'>
            <IconShield size={16} />
            <span className='small fw-semibold text-uppercase'>Guardian {activeTierOption.label}</span>
          </div>
        )}

        {activeTierOption && <div className='vr' />}

        {/* Period — inline in the toolbar */}
        <div className='guardian-filters__inline'>
          <span className='small text-muted mb-0'>{FIELD_CONFIG.period.label}</span>
          <div className='guardian-filters__inline-select'>{FIELD_CONFIG.period.render()}</div>
        </div>

        {/* Divider */}
        {guardianTiersOptions.length > 0 && <div className='vr' />}

        {/* Tier switcher badges */}
        {guardianTiersOptions.length > 0 && (
          <div className='guardian-filters__tiers d-flex align-items-center flex-wrap gap-2'>
            <span className='small text-muted'>
              <Trans>Tier</Trans>
            </span>
            {guardianTiersOptions.map((opt) => {
              const isActive = activeTier === opt.value
              return (
                <button
                  key={opt.value}
                  type='button'
                  className={`btn btn-sm d-flex align-items-center gap-1 ${isActive ? `bg-${opt.color} text-white border-0` : `btn-outline-${opt.color}`}`}
                  aria-pressed={isActive}
                  onClick={() => handleTierSelect(opt.value)}
                >
                  <IconShield size={14} />
                  {opt.label}
                </button>
              )
            })}
          </div>
        )}

        <div className='vr' />

        {/* Filters — toggles the collapsible panel below */}
        {advancedFields.length > 0 && (
          <button
            type='button'
            className={`btn btn-sm guardian-toolbar-btn d-flex align-items-center gap-2 ${
              activeAdvancedCount > 0 ? 'guardian-filters__toggle-active' : 'btn-link'
            }`}
            data-bs-toggle='collapse'
            data-bs-target='#guardian-filters-panel'
            aria-controls='guardian-filters-panel'
            aria-expanded='false'
          >
            <IconFilter size={16} />
            <Trans>Filters</Trans>
            {activeAdvancedCount > 0 && <span className='badge'>{activeAdvancedCount}</span>}
          </button>
        )}

        {/* 7. Collapsible panel — full-width row below the toolbar */}
        {advancedFields.length > 0 && (
          <div className='collapse guardian-filters__panel' id='guardian-filters-panel'>
            <div className='guardian-filters__panel-inner'>
              <div className='guardian-filters__grid'>
                {advancedFields.map((field) => {
                  const config = FIELD_CONFIG[field]
                  if (!config) return null
                  return (
                    <div key={field} className='guardian-filters__field'>
                      <div className='small fw-semibold mb-1'>{config.label}</div>
                      {config.render()}
                    </div>
                  )
                })}
              </div>
              <div className='guardian-filters__panel-actions'>
                <button type='button' className='btn btn-sm btn-outline-secondary' onClick={handleReset}>
                  <Trans>Reset filters</Trans>
                </button>
                <button type='button' className='btn btn-sm btn-primary' onClick={handleApply}>
                  <Trans>Apply filters</Trans>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden inputs — must stay inside the form to carry submitted values */}
      <div className='guardian-filters-inputs'>
        <input type='hidden' name='query[period]' value={filters.period || ''} />
        {visibleFilters.includes('account_ids') &&
          filters.account_ids?.map((id, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`aws-${index}`} type='hidden' name='query[account_ids][]' value={id} />
          ))}
        {visibleFilters.includes('environments') &&
          filters.environments?.map((env, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`env-${index}`} type='hidden' name='query[environments][]' value={env} />
          ))}
        {visibleFilters.includes('reporter_types') &&
          filters.reporter_types?.map((type, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`reporter-type-${index}`} type='hidden' name='query[reporter_types][]' value={type} />
          ))}
        {visibleFilters.includes('issue_priorities') &&
          filters.issue_priorities?.map((priority, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`priority-${index}`} type='hidden' name='query[issue_priorities][]' value={priority} />
          ))}
        {visibleFilters.includes('issue_types') &&
          filters.issue_types?.map((type, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`type-${index}`} type='hidden' name='query[issue_types][]' value={type} />
          ))}
        {visibleFilters.includes('request_types') &&
          filters.request_types?.map((rt, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`request-type-${index}`} type='hidden' name='query[request_types][]' value={rt} />
          ))}
        {visibleFilters.includes('issue_reporters') &&
          filters.issue_reporters?.map((reporter, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`reporter-${index}`} type='hidden' name='query[issue_reporters][]' value={reporter} />
          ))}
        {visibleFilters.includes('issue_assignees') &&
          filters.issue_assignees?.map((assignee, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
            <input key={`assignee-${index}`} type='hidden' name='query[issue_assignees][]' value={assignee} />
          ))}
        {filters.guardian_tiers?.map((tier, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
          <input key={`tier-${index}`} type='hidden' name='query[guardian_tiers][]' value={tier} />
        ))}
      </div>
    </>
  )
}

export default class GuardianFiltersController extends ReactController {
  static values = {
    currentTab: String,
    initialOptions: Object,
    initialValues: Object,
  }

  createComponent() {
    const submit = () => {
      this.element.requestSubmit()
    }

    return (
      <GuardianFilters
        currentTab={this.currentTabValue}
        initialOptions={this.initialOptionsValue}
        initialValues={this.initialValuesValue}
        onChange={submit}
      />
    )
  }
}
